import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCallback } from 'react';
import { QueryKey, UseQueryOptions } from 'react-query';
import type { Paginated, QuotationSupplier, QuotationSupplierProduct } from './types';
import { restaurantInquiriesEndpoint } from './use-create-restaurant-inquiry-api';

// GET /cms/restaurant-inquiries/quotation-suppliers — searchable list of
// suppliers eligible to be quoted (backed by supplier_profiles). This is step 1
// of the create-quote flow; step 2 is .../quotation-suppliers/:supplierId/products.
// Exported so product-create flows can invalidate the catalog caches by prefix.
export const quotationSuppliersEndpoint = `${restaurantInquiriesEndpoint}/quotation-suppliers`;

export interface QuotationSupplierOption {
  value: number; // supplierId (NOT the profile id)
  label: string;
  raw: QuotationSupplier;
}

export function useSearchQuotationSuppliersApi() {
  const loadOptions = useCallback((inputValue: string, callback: (options: QuotationSupplierOption[]) => void) => {
    http
      .get(quotationSuppliersEndpoint, {
        params: { page: 1, limit: 20, search: inputValue || undefined },
      })
      .then(({ data }) => {
        const list: QuotationSupplier[] = data?.data ?? [];
        callback(
          list.map((s) => ({
            value: s.supplierId,
            label: s.supplierName || s.supplier?.name || `Supplier #${s.supplierId}`,
            raw: s,
          }))
        );
      })
      .catch(() => callback([]));
  }, []);

  return { loadOptions };
}

export interface QuotationSupplierProductOption {
  value: number; // the master product id for POST .../quotes
  label: string;
  raw: QuotationSupplierProduct;
}

// Server-side search (family name or brand) over every active master product,
// the ones this supplier prices first (the backend's order), for the
// ProductMatcher's async select. Dependent-dropdown pattern: loadOptions(supplierId)
// returns the (inputValue, callback) function.
export function useSearchQuotationSupplierProductsApi() {
  const loadOptions = useCallback(
    (supplierId: number) => (inputValue: string, callback: (options: QuotationSupplierProductOption[]) => void) => {
      http
        .get(`${quotationSuppliersEndpoint}/${supplierId}/products`, {
          params: { page: 1, limit: 20, search: inputValue || undefined },
        })
        .then(({ data }) => {
          const list: QuotationSupplierProduct[] = data?.data ?? [];
          callback(list.map((p) => ({ value: p.id, label: p.nameEn || `Master product #${p.id}`, raw: p })));
        })
        .catch(() => callback([]));
    },
    []
  );

  return { loadOptions };
}

// GET .../quotation-suppliers/:supplierId/products — the master products the
// supplier can be quoted for, the ones it prices first. The `id` of a row is the
// master product id for POST .../quotes. 404 if supplier unknown.
type TData = Paginated<QuotationSupplierProduct>;
type TError = AxiosError;
type Options = Omit<UseQueryOptions<TData, TError, TData, QueryKey>, 'queryKey' | 'queryFn'>;

export function useGetQuotationSupplierProductsApi(
  supplierId: number | null | undefined,
  search?: string,
  options?: Options
) {
  return useQueryWrapper<TData, TError>(
    [quotationSuppliersEndpoint, supplierId, 'products', { search }],
    async () => {
      const response = await http.get(`${quotationSuppliersEndpoint}/${supplierId}/products`, {
        params: { page: 1, limit: 50, search: search || undefined },
      });
      return response.data;
    },
    { enabled: !!supplierId, keepPreviousData: true, ...options }
  );
}
